import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

function usePrevious(value) {
  const ref = useRef<typeof value>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function debounce(func, wait, immediate?) {
  let timeout;

  return (...args) => {
    const later = () => {
      timeout = null;
      if (!immediate) func(args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(args);
  };
}

export const ClampLines = (props) => {
  const element = useRef<HTMLElement>(null);
  const uuid = props.id;
  let start = 0;
  let middle = 0;
  let end = 0;
  const [watch, setWatch] = useState(true);
  const [isCalculatingClampChar, setIsCalculatingClampChar] = useState(true);
  const [lineHeight, setLineHeight] = useState(0);
  const [original, setOriginal] = useState(props.text);
  const [expanded, setExpanded] = useState(true);
  const [noClamp, setNoClamp] = useState(false);
  const [text, setText] = useState(props.text.substring(0, 20));

  const ssr = typeof window === 'undefined';

  const getEllipsis = () => {
    return watch && !noClamp ? props.ellipsis : '';
  };

  const moveMarkers = (maxHeight) => {
    if (!element.current) return;
    if (element.current.clientHeight <= maxHeight) {
      start = middle + 1;
    } else {
      end = middle - 1;
    }
  };

  const clampLines = () => {
    if (!element.current || !lineHeight) return;

    const maxHeight = lineHeight * props.lines + 1;

    start = 0;
    middle = 0;
    end = original.length;

    while (start <= end) {
      middle = Math.floor((start + end) / 2);
      element.current.innerText = original.slice(0, middle);
      if (middle === original.length) {
        setText(original);
        setNoClamp(true);
        setIsCalculatingClampChar(false);
        return;
      }

      moveMarkers(maxHeight);
    }

    element.current.innerText =
      original.slice(0, middle - 5 - props.moreText.length) + getEllipsis();
    setText(original.slice(0, middle - 5 - props.moreText.length) + getEllipsis());
    setIsCalculatingClampChar(false);
  };

  const handleResize = () => {
    if (watch) {
      setNoClamp(false);
      setExpanded((expanded) => !expanded);
      setIsCalculatingClampChar(true);
    }
  };

  let debouncedHandleResize = handleResize;
  if (!ssr) {
    debouncedHandleResize = debounce(handleResize, props.delay);
  } else {
    setText(props.text.substring(0, 20));
  }

  useEffect(() => {
    if (props.text && !ssr && element.current) {
      if (lineHeight === 0) {
        setLineHeight(element.current.clientHeight + 1);
      }

      setIsCalculatingClampChar(true);

      if (watch) {
        window.addEventListener('resize', debouncedHandleResize);
      }
    }
    return () => {
      if (!ssr) {
        window.removeEventListener('resize', debouncedHandleResize);
      }
    };
  }, [element.current]);

  useEffect(() => {
    if (watch && isCalculatingClampChar) {
      clampLines();
      setExpanded((expanded) => !expanded);
      if (props.onHide) props.onHide();
    } else if (isCalculatingClampChar) {
      if (props.onShowOverride && props.onShow) {
        setIsCalculatingClampChar(false);
        setExpanded(false);
        setWatch(true);
      } else {
        setText(original);
        setExpanded((expanded) => !expanded);
      }
      if (props.onShow) props.onShow();
    } else {
      return;
    }
  }, [watch, isCalculatingClampChar, lineHeight]);

  const prevProps = usePrevious(props);

  useEffect(() => {
    if (prevProps?.text !== props.text) {
      setOriginal(props.text);
      setIsCalculatingClampChar(true);
    }
  }, [prevProps?.text, props?.text]);

  if (!props.text) {
    return null;
  }

  const clickHandler = (e) => {
    const { stopPropagation } = props;

    e.preventDefault();
    stopPropagation && e.stopPropagation();

    setIsCalculatingClampChar(true);
    setWatch((watch) => !watch);
  };

  const getButton = () => {
    if (noClamp || !props.buttons) return;

    const buttonText = watch ? props.moreText : props.lessText;

    return (
      <button
        className="clamp-lines__button"
        onClick={clickHandler}
        aria-controls={`clamped-content-${uuid}`}
        aria-expanded={!expanded}
        style={{ display: 'inline' }}
      >
        {buttonText}
      </button>
    );
  };

  const innerClampElement = React.createElement(
    props.innerElement,
    {
      ref: element,
      id: `clamped-content-${uuid}`,
      'aria-hidden': expanded,
      style: { display: isCalculatingClampChar ? 'block' : 'inline' },
    },
    text
  );

  const getClassName = () => {
    const className = props.className || '';

    return `clamp-lines ${className}`;
  };

  return (
    <div className={getClassName()}>
      {innerClampElement} {getButton()}
    </div>
  );
};

ClampLines.propTypes = {
  text: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  lines: PropTypes.number,
  ellipsis: PropTypes.string,
  buttons: PropTypes.bool,
  moreText: PropTypes.string,
  lessText: PropTypes.string,
  className: PropTypes.string,
  delay: PropTypes.number,
  stopPropagation: PropTypes.bool,
  innerElement: PropTypes.string,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  onShowOverride: PropTypes.bool,
};

ClampLines.defaultProps = {
  lines: 3,
  ellipsis: '...',
  buttons: true,
  moreText: 'Read more',
  lessText: 'Read less',
  delay: 300,
  innerElement: 'div',
  onShowOverride: false,
};
